import type { MappingLeafError } from "@shared-core/data/fulfilment-route-types";
import { CircleAlert } from "lucide-react";

/**
 * One leaf of an item's mapping tree on one back-end — values only (Ben,
 * 24 Sep: mappings are code with no lifecycle). The value as the back-end
 * names it, or "No Solopress equivalent · <reason>", or "Omitted" when the
 * option deliberately sends nothing there, or nothing when the item has no
 * subtree there. The only annotation is a red error mark on a
 * leaf that failed last night's run, with the error the run recorded.
 */
export function MappingLeafView({
  backEndLabel,
  attribute,
  value,
  noEquivalentReason = null,
  isOmitted = false,
  error = null,
}: {
  backEndLabel: string;
  /** The slot or attribute, shown before the value (`ProductType`,
   *  `material`); omitted where the column already says it. */
  attribute?: string;
  /** Null when there is no mapping here at all. */
  value: string | null;
  noEquivalentReason?: string | null;
  /** The option deliberately sends nothing to this back-end. */
  isOmitted?: boolean;
  error?: MappingLeafError | null;
}) {
  return (
    <div className="space-y-0.5 text-sm">
      {noEquivalentReason !== null ? (
        <p className="text-muted-foreground">
          <span className="italic">No {backEndLabel} equivalent</span> ·{" "}
          {noEquivalentReason}
        </p>
      ) : isOmitted ? (
        <p className="italic text-muted-foreground">Omitted</p>
      ) : value === null ? (
        <p className="text-muted-foreground">
          <span aria-hidden="true">—</span>
          <span className="sr-only">Not mapped on {backEndLabel}</span>
        </p>
      ) : (
        <p className="flex flex-wrap items-baseline gap-x-1.5">
          {attribute && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {attribute}
            </span>
          )}
          <span className={error ? "text-destructive" : undefined}>
            {value}
          </span>
        </p>
      )}
      {error && (
        <p className="flex items-start gap-1 text-xs text-destructive">
          <CircleAlert className="mt-0.5 size-3 shrink-0" aria-hidden="true" />
          <span>
            <span className="sr-only">Error: </span>
            {error.message}
          </span>
        </p>
      )}
    </div>
  );
}
